import { Button, Card, Col, Form, Input, Row, Space } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import BaseCellText from 'common/components/table/base-cell-text';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import TableBase from 'common/components/table/table-base';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import { ICellRendererProps, ITableControls } from 'common/components/table/table.types';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { formatCustomDateTime, StrToDateTimeLocal } from 'common/helpers/date.helper';
import dailyActivityReportService from 'common/services/daily-activity-service/daily-activity-report.servcie';
import { IDailyActivityReportRead } from 'common/services/daily-activity-service/daily-activity-report.types';
import './daily-activity-reports.scss';
import { trackPromise } from 'react-promise-tracker';
import { notification } from 'common/utils/notification';
import { ReactComponent as FilterIcon } from 'assets/icons/filtericon.svg';
import { ColumnFilter } from 'common/components/table/filters/column-filter/column-filter';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import dayjs from 'dayjs';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { useEffect, useRef, useState } from 'react';
import { IConfirmDeleteModalProps } from 'common/components/modal/confirm-delete-modal/confirm-delete-modal.types';
import ConfirmDeleteModal from 'common/components/modal/confirm-delete-modal/confirm-delete-modal';
import { CloudDownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import { saveAs } from 'file-saver';
import { usePDF } from '@react-pdf/renderer';
import PrintDailyActivityReports from './print-daily-activity-reports/print-daily-activity-reports';
import PrintDailyActivityReportFilterModal from './print-daily-activity-reports/print-daily-activity-reports-filter.modal';
import { PrintActivityReportType } from './print-daily-activity-reports/print-daily-activity-reports.types';
import ReporterIcon from 'assets/icons/eiq-reporter.png';
import configurationStore from 'common/stores/configuration/configuration.store';
import { getBase64 } from 'common/helpers/getBase64.helper';

interface IDailyActivityReportsProps {
  showDeleteButton: boolean;
  canCreateNew: boolean;
}

export const DailyActivityRepors = ({ showDeleteButton, canCreateNew }: IDailyActivityReportsProps) => {
  const [currentSelectedRow, setCurrentSelectedRow] = useState<IDailyActivityReportRead | undefined>(undefined);

  const resetHandler = (tableContext: ITableControls<any>) => {
    tableContext.resetFilter('AND');
  };

  const filters = (
    <ColumnFilter
      width={258}
      buttonType="ghost"
      classNameContent="so-column-filter"
      buttonIcon={<FilterIcon />}
      buttonText={''}
      onResetFilters={resetHandler}
      onBeforeApply={resetHandler}
      filterConfig={[
        {
          name: 'timestamp',
          filterType: FilterTypesEnum.Equals,
          label: 'Date & Time',
          type: {
            nameEnd: 'timestamp',
            type: 'DATE_TIME_RANGE',
          },
          onChange(filterValue, tableContext) {
            if (filterValue) {
              tableContext.updateFilter(
                'timestamp',
                [
                  { operator: FilterTypesEnum.GreaterThanOrEqual, value: dayjs(filterValue.start).utc().valueOf(), innerFilterType: 'AND' },
                  { operator: FilterTypesEnum.LessThan, value: dayjs(filterValue.end).utc().valueOf() },
                ],
                'AND',
              );
            }
          },
        },
      ]}
    />
  );

  const tableControls = useTableControls({
    onSelectedRow: (selectedRow: IDailyActivityReportRead) => {
      setCurrentSelectedRow(selectedRow);
    },
    onFetch: dailyActivityReportService.getReports,
    initialColumns: dailyActivityColumns,
  });

  const [form] = Form.useForm();

  const onFinish = async (values: any) => {
    await trackPromise(dailyActivityReportService.create(values.dispatcher, values.entryDetails));
    notification.success({ message: 'Report added', duration: 5 });
    form.resetFields();
    tableControls.refetchData();
  };

  const printType = useRef<PrintActivityReportType | undefined>();

  const printFilterModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const data = {
        printType: printType.current,
      };

      setModalInitData(data);
    },
    onClose: async (isSuccess?: boolean, from?: number, to?: number) => {
      if (!isSuccess) {
        return;
      }

      if (printType.current === 'Print') {
        await handlePrint(from!, to!);
      } else {
        await handleExport(from!, to!);
      }
    },
  });

  const deleteReportModal = useModal({
    onBeforeShow: (setModalInitData) => {
      if (!currentSelectedRow?.id) {
        return;
      }

      const data: IConfirmDeleteModalProps = {
        message: 'Are you sure you want to delete this activity report?',
        onDelete: () => {
          dailyActivityReportService.delete(currentSelectedRow.id).then(() => {
            deleteReportModal.hideModal(true);
          });
        },
      };

      setModalInitData(data);
    },
    onClose: (isSuccess: boolean | undefined) => {
      if (isSuccess) {
        tableControls.setSelectedRow(null);
        tableControls.refetchData();
        notification.success({ message: 'Report deleted', duration: 5 });
      }
    },
  });

  const iframeRef = useRef<any>(null);
  const isPdfReady = useRef<boolean>(false);
  const [pdfInstance, updatePdfInstance] = usePDF();
  const pdfInstanceRef = useRef(pdfInstance);

  useEffect(() => {
    if (!pdfInstance.loading && pdfInstance.url && pdfInstance.blob) {
      isPdfReady.current = true;
    } else {
      isPdfReady.current = false;
    }

    pdfInstanceRef.current = pdfInstance;
  }, [pdfInstance]);

  const waitForPdfReady = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const timer = setInterval(() => {
        if (isPdfReady.current) {
          clearInterval(timer);
          resolve();
        }

        if (Date.now() - start > 20000) {
          clearInterval(timer);
          reject(new Error('PDF generation timeout'));
        }
      }, 200);
    });
  };

  const generatePdfForRow = async (data: IDailyActivityReportRead[], dateFrom: number, dateTo: number): Promise<void> => {
    const logoIconBase64 = await configurationStore.getCommunityLogoOrDefaultForPDF();
    const reportIconBase64 = await getBase64(ReporterIcon);
    updatePdfInstance(
      <PrintDailyActivityReports
        data={data}
        dateFrom={dateFrom}
        dateTo={dateTo}
        logoIconBase64={logoIconBase64}
        reportIconBase64={reportIconBase64}
      />,
    );
    await waitForPdfReady();
  };

  const onPrintButtonClicked = () => {
    printType.current = 'Print';
    printFilterModal.showModal();
  };

  const handlePrint = async (from: number, to: number) => {
    const data = await trackPromise(pullData(from, to));
    await trackPromise(generatePdfForRow(data, from, to));
    const iframe = iframeRef.current;
    if (!iframe || !pdfInstanceRef.current.url) {
      return;
    }
    iframe.src = pdfInstanceRef.current.url;
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };
  };

  const pullData = async (from: number, to: number) => {
    let result: IDailyActivityReportRead[] = [];

    const pageLimit = { page: 1, limit: 10_000 };
    const filter = `timestamp:>=${from},timestamp:<${to}`;
    const sortBy = tableControls.getSortBy();

    let offset = 0;

    while (true) {
      const data = await dailyActivityReportService.getReports({ ...pageLimit, offset: offset }, filter, sortBy);

      if (data.items.length) {
        result = [...result, ...data.items];
        offset += data.items.length;
      }

      if (data.totalCount <= result.length) {
        break;
      }
    }

    return result;
  };

  const onExportButtonClicked = async () => {
    printType.current = 'Export';
    printFilterModal.showModal();
  };

  const handleExport = async (from: number, to: number) => {
    const data = await trackPromise(pullData(from, to));
    await trackPromise(generatePdfForRow(data, from, to));
    if (pdfInstanceRef.current.blob) {
      saveAs(
        pdfInstanceRef.current.blob,
        `Daily Activity Reports ${formatCustomDateTime(from / 1000, true)} - ${formatCustomDateTime(to / 1000, true)}.pdf`,
      );
    }
  };

  return (
    <>
      <iframe ref={iframeRef} style={{ width: 0, height: 0, border: 'none' }} title="pdf-print-frame" />
      <div className="incident-reports-page">
        <Row gutter={24}>
          {canCreateNew && (
            <Col xs={8}>
              <Card className="eiq-card eiq-card-border" style={{ width: '100%' }}>
                <Row justify="space-between" className="content-title">
                  <Space>
                    <h2 className="main-title">Daily Activity Reports</h2>
                  </Space>
                  <FormWrapper
                    form={form}
                    autoComplete="false"
                    layout="vertical"
                    onFinish={onFinish}
                    className="activity-report-form-wrapper">
                    <Space className="full-width daily-activity-form" size={8}>
                      <Form.Item
                        rules={formRules.dispatcher}
                        name="dispatcher"
                        label={
                          <div className="daily-activity-report-label">
                            <div className="title">Your Name</div>
                          </div>
                        }>
                        <Input placeholder="Enter your name" />
                      </Form.Item>
                      <Form.Item
                        rules={formRules.entryDetails}
                        name="entryDetails"
                        label={
                          <div className="daily-activity-report-label">
                            <div className="title">Report / Log Entry</div>
                            <div className="description">Submit a new entry for the activity reports.</div>
                          </div>
                        }>
                        <TextArea placeholder="Enter report / log entry" style={{ height: 150 }} />
                      </Form.Item>
                    </Space>
                    <Button style={{ marginLeft: 'auto' }} type="primary" icon={<PlusOutlined />} htmlType="submit">
                      Submit Entry
                    </Button>
                  </FormWrapper>
                </Row>
              </Card>
            </Col>
          )}
          <Col flex="auto">
            <Card className="eiq-card eiq-card-border" style={{ width: '100%' }}>
              <TableContextProvider controls={tableControls}>
                <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
                  <Col flex="auto">
                    <SearchFilter
                      suffix={filters}
                      adaptiveSearch={true}
                      enableProgressiveSearch={true}
                      placeholder="Search entries by name, location or keywords"
                      rulesForColumn={{
                        dispatcher: FilterTypesEnum.StartWith,
                        entryDetails: FilterTypesEnum.Contains,
                      }}
                      rightSide={
                        <Space size={8}>
                          <Button onClick={onExportButtonClicked} icon={<CloudDownloadOutlined />}>
                            Export
                          </Button>

                          <Button onClick={onPrintButtonClicked} icon={<PrintIcon />}>
                            Print
                          </Button>
                          {showDeleteButton && (
                            <Button
                              icon={<DeleteIcon stroke="#505762" />}
                              size="middle"
                              onClick={deleteReportModal.showModal}
                              className="mobile-btn">
                              Delete
                            </Button>
                          )}
                        </Space>
                      }
                    />
                  </Col>
                </Row>
                <div style={{ height: 'calc(100vh - 180px)' }}>
                  <TableBase selectFirstRowByDefault estimatedRowHeight={120} />
                </div>
              </TableContextProvider>
              <ConfirmDeleteModal
                isEdit={false}
                initData={deleteReportModal?.initData}
                title="Delete"
                isOpen={deleteReportModal?.isOpenModal}
                onClose={deleteReportModal?.hideModal}
              />
              <PrintDailyActivityReportFilterModal
                initData={printFilterModal.initData}
                isOpen={printFilterModal.isOpenModal}
                title={`${printType.current} Date and Time Range`}
                onClose={printFilterModal.hideModal}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

const dailyActivityColumns = [
  {
    key: 'timestamp',
    title: 'Date & Time',
    dataKey: 'timestamp',
    width: 240,
    minWidth: 150,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<IDailyActivityReportRead>) => {
      return <BaseCellText>{formatCustomDateTime(cell?.rowData?.timestamp / 1000, true)}</BaseCellText>;
    },
  },
  {
    key: 'dispatcher',
    title: 'Dispatcher',
    dataKey: 'dispatcher',
    width: 180,
    minWidth: 100,
    resizable: true,
    sortable: true,
  },
  {
    key: 'entryDetails',
    title: 'Entry Details',
    dataKey: 'entryDetails',
    width: 600,
    minWidth: 100,
    resizable: true,
    sortable: false,
  },
];

const formRules = {
  dispatcher: [{ required: true, message: 'Name is required!' }],
  entryDetails: [{ required: true, message: 'Report / log entry is required!' }],
};
